<script>
import { mapGetters } from 'vuex';
import GclickAPI from 'dashboard/api/gclick';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0, gclickFrame: null };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    gclickConfig() {
      if (!this.gclickFrame?.enabled) return [];
      return [{ type: 'frame', url: this.gclickFrame.url }];
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showConversationTabs() {
      return this.dashboardApps.length && this.currentChat.id;
    },
    showGclickPanel() {
      return this.gclickConfig.length && this.currentChat.id;
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    this.fetchGclickFrame();
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    async fetchGclickFrame() {
      try {
        const { data } = await GclickAPI.frame();
        this.gclickFrame = data;
      } catch {
        this.gclickFrame = null;
      }
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full h-full relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      class="!flex-none"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
    />
    <div class="flex flex-1 min-h-0 min-w-0">
      <div class="relative flex flex-col flex-1 min-h-0 min-w-0">
        <woot-tabs
          v-if="showConversationTabs"
          :index="activeIndex"
          class="h-10"
          @change="onDashboardAppTabChange"
        >
          <woot-tabs-item
            v-for="tab in dashboardAppTabs"
            :key="tab.key"
            :index="tab.index"
            :name="tab.name"
            :show-badge="false"
            is-compact
          />
        </woot-tabs>
        <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
          <MessagesView
            v-if="currentChat.id"
            :inbox-id="inboxId"
            :is-inbox-view="isInboxView"
          />
          <EmptyState
            v-if="!currentChat.id && !isInboxView"
            :is-on-expanded-layout="isOnExpandedLayout"
          />
        </div>
        <slot />
        <DashboardAppFrame
          v-for="(dashboardApp, index) in dashboardApps"
          v-show="activeIndex - 1 === index"
          :key="currentChat.id + '-' + dashboardApp.id"
          :is-visible="activeIndex - 1 === index"
          :config="dashboardApps[index].content"
          :position="index"
          :current-chat="currentChat"
        />
      </div>
      <div
        v-if="showGclickPanel"
        class="min-h-0 w-[320px] min-w-[320px] shrink-0 overflow-hidden border-l border-n-weak"
      >
        <DashboardAppFrame
          :key="'gclick-' + currentChat.id"
          is-visible
          :config="gclickConfig"
          :position="0"
          :current-chat="currentChat"
          class="h-full"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.bg-conversation-box {
  background-color: #eef0f8;
}
</style>
